import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Trash2,
  Loader2,
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
  Car,
  AlertCircle,
  Home
} from 'lucide-react';
import type { ManualStopKindFilter, StagedManualStop } from '../types/map.types';
import { RecallCampaignCard } from './RecallCampaignCard';
import { CustomerContactReveal } from '@/components/CustomerContactReveal';
import { getManualStopReasonLabel } from '@/lib/validation/manualStopValidation';
import {
  getStopDisplayName,
  getStopLocationKey,
  isCustomerStop
} from '@/lib/manualStops/locationKey';

interface ManualStopsListProps {
  stops: StagedManualStop[];
  onRemove: (id: string) => Promise<void>;
  assignedStops?: Map<string, { technicianName: string }>;
  /** Location key (getStopLocationKey) to expand and scroll into view */
  scrollToLocation?: string | null;
  /** Business / customer filter, shown only when both kinds are present */
  kindFilter?: ManualStopKindFilter;
  onKindFilterChange?: (filter: ManualStopKindFilter) => void;
  /** Counts before the kind filter is applied */
  kindCounts?: { business: number; customer: number };
  /** Called after the scroll-to completes so the parent can clear its trigger */
  onScrollComplete?: () => void;
}

interface LocationGroupData {
  /** getStopLocationKey — never a customer's name */
  key: string;
  name: string;
  isCustomer: boolean;
  contactId?: string | null;
  addressLine2?: string | null;
  dispatcherNote?: string | null;
  /** USPS check didn't confirm the address (advisory only) */
  addressUnverified: boolean;
  address: string;
  city?: string;
  region?: string;
  postcode?: string;
  phone?: string;
  /** Map of VIN (or fallback to stop.id when no VIN) → array of stop rows (one per campaign) */
  vehicles: Map<string, StagedManualStop[]>;
}

const UNVERIFIED_STATUSES = new Set(['unconfirmed', 'unit_missing', 'unavailable']);

const UNASSIGN_FIRST = 'Unassign it from the technician first';

const formatFullAddress = (loc: LocationGroupData): string => {
  const parts = [loc.addressLine2 ? `${loc.address}, ${loc.addressLine2}` : loc.address];
  if (loc.city) parts.push(loc.city);
  if (loc.region) parts.push(loc.region);
  const joined = parts.filter(Boolean).join(', ');
  return loc.postcode ? `${joined} ${loc.postcode}` : joined;
};

// ===========================================================================
// VehicleCard — one vehicle (by VIN), may contain multiple recall campaigns
// ===========================================================================
interface VehicleCardProps {
  stops: StagedManualStop[];
  index: number;
  onRemove: () => void;
  isRemoving: boolean;
  assignedStops?: Map<string, { technicianName: string }> | undefined;
}

// eslint-disable-next-line max-lines-per-function, complexity
const VehicleCard: React.FC<VehicleCardProps> = ({
  stops,
  index,
  onRemove,
  isRemoving,
  assignedStops
}) => {
  const [copied, setCopied] = useState(false);
  const firstStop = stops[0];
  if (!firstStop) return null;

  const hasVehicleInfo = firstStop.vehicle_year && firstStop.vehicle_make;
  const hasRealVin = firstStop.vin && !firstStop.vin.startsWith('MANUAL-');

  // Any campaign for this vehicle assigned? Use its technician name for the tooltip
  const assignedRow = stops.find(s => {
    const k = `${s.vin}|${s.campaign_number}`;
    return assignedStops?.has(k);
  });
  const assignmentInfo = assignedRow
    ? assignedStops?.get(`${assignedRow.vin}|${assignedRow.campaign_number}`)
    : undefined;
  const isAssigned = !!assignmentInfo;

  // Notes — take first non-empty (stops within a VIN group should share the same note)
  const notes = stops.map(s => s.manual_stop_notes).find(n => n && n.trim());

  // Campaign rows — filter out placeholder MANUAL entries
  const campaignStops = stops.filter(
    s =>
      s.campaign_number &&
      s.campaign_number !== 'MANUAL' &&
      !s.campaign_number.startsWith('MANUAL-')
  );

  const hasBottomContent = isAssigned || !!notes || campaignStops.length > 0;

  const handleCopyVin = async () => {
    if (!firstStop.vin) return;
    try {
      await navigator.clipboard.writeText(firstStop.vin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy VIN:', err);
    }
  };

  return (
    <div className='border rounded-lg bg-card overflow-hidden'>
      <div className='flex items-start gap-3 p-3'>
        <span
          className={
            'flex items-center justify-center w-6 h-6 rounded-full ' +
            'text-xs font-bold text-white shrink-0 mt-0.5'
          }
          style={{ backgroundColor: '#0ea5e9' }}
        >
          {index + 1}
        </span>

        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='font-semibold text-sm truncate'>
              {hasVehicleInfo
                ? `${firstStop.vehicle_year} ${firstStop.vehicle_make?.toUpperCase() || ''} ` +
                  `${firstStop.vehicle_model || ''}`
                : 'Manual Stop'}
            </span>
            <Badge
              variant='outline'
              className='bg-[#0ea5e9]/10 text-[#0ea5e9] border-[#0ea5e9]/30 font-medium shrink-0'
            >
              {getManualStopReasonLabel(firstStop.manual_stop_reason)}
            </Badge>
          </div>
          {hasRealVin && (
            <div className='flex items-center gap-1.5 mt-1'>
              <span className='font-mono text-xs text-muted-foreground truncate'>
                {firstStop.vin}
              </span>
              <button
                onClick={handleCopyVin}
                className='p-0.5 hover:bg-muted rounded transition-colors shrink-0'
                title='Copy VIN'
              >
                {copied ? (
                  <Check className='h-3.5 w-3.5 text-green-500' />
                ) : (
                  <Copy className='h-3.5 w-3.5 text-muted-foreground hover:text-foreground' />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Removing an assigned stop would pull it out from under the technician (and a
            later release could lose it), so it must be unassigned first. The title sits on
            the wrapper because a disabled button gets no hover. */}
        <span className='shrink-0' title={isAssigned ? UNASSIGN_FIRST : undefined}>
          <Button
            variant='ghost'
            size='sm'
            onClick={onRemove}
            disabled={isRemoving || isAssigned}
            aria-label={isAssigned ? UNASSIGN_FIRST : 'Remove stop'}
            className={
              'h-8 w-8 p-0 text-destructive ' + 'hover:text-destructive hover:bg-destructive/10'
            }
          >
            {isRemoving ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Trash2 className='h-4 w-4' />
            )}
          </Button>
        </span>
      </div>

      {hasBottomContent && (
        <div className='px-3 pb-3 pl-12 space-y-2'>
          {(isAssigned || notes) && (
            <div className='flex items-center gap-2 flex-wrap'>
              {isAssigned && (
                <Badge
                  variant='outline'
                  className={
                    'bg-green-100 text-green-700 border-green-300 ' +
                    'dark:bg-green-900/40 dark:text-green-300'
                  }
                  title={`Assigned to ${assignmentInfo?.technicianName || 'technician'}`}
                >
                  Assigned
                </Badge>
              )}
              {notes && <span className='text-xs text-muted-foreground truncate'>{notes}</span>}
            </div>
          )}
          {campaignStops.map(s => (
            <RecallCampaignCard
              key={s.id}
              campaignNumber={s.campaign_number}
              recallTitle={s.recall_title}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// LocationGroup — collapsible section, one per location key (household or business)
// ===========================================================================
interface LocationGroupProps {
  location: LocationGroupData;
  onRemoveVehicle: (ids: string[]) => Promise<void>;
  removingIds: Set<string>;
  assignedStops?: Map<string, { technicianName: string }> | undefined;
  /** When true, auto-expand this group. Used by parent to reveal a map-clicked stop. */
  openTrigger?: number;
  outerRef?: (el: HTMLDivElement | null) => void;
}

// eslint-disable-next-line max-lines-per-function
const LocationGroup: React.FC<LocationGroupProps> = ({
  location,
  onRemoveVehicle,
  removingIds,
  assignedStops,
  openTrigger,
  outerRef
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Parent sets a new trigger value → auto-expand this group
  useEffect(() => {
    if (openTrigger !== undefined && openTrigger > 0) {
      setIsOpen(true);
    }
  }, [openTrigger]);

  const vehicleEntries = Array.from(location.vehicles.entries());
  const vehicleCount = vehicleEntries.length;
  // "Jobs" = total stop rows at this location (each recall / oil change / etc. is one job)
  const jobCount = vehicleEntries.reduce((sum, [, stops]) => sum + stops.length, 0);
  const fullAddress = formatFullAddress(location);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div
        ref={outerRef}
        className={
          'border border-sky-200/70 dark:border-sky-800/60 rounded-lg ' +
          'bg-sky-50/30 dark:bg-sky-950/10 overflow-hidden'
        }
      >
        <CollapsibleTrigger asChild>
          <button
            className={
              'w-full flex items-start gap-3 p-4 hover:bg-muted/50 transition-colors text-left'
            }
          >
            <div className='flex-1 min-w-0'>
              <div className='font-semibold truncate flex items-center gap-1.5'>
                {location.isCustomer && (
                  <Home className='h-4 w-4 text-[#0ea5e9] shrink-0' aria-hidden />
                )}
                {location.name}
              </div>
              {location.phone && (
                <div className='text-sm text-muted-foreground mt-0.5'>{location.phone}</div>
              )}
              <div className='text-sm text-muted-foreground mt-0.5 break-words'>{fullAddress}</div>
              {location.addressUnverified && (
                <div className='text-xs text-muted-foreground mt-0.5'>Address not verified</div>
              )}
              <div className='flex gap-1.5 flex-wrap mt-2'>
                <Badge
                  variant='outline'
                  className={
                    'text-xs font-medium bg-sky-50 text-sky-700 border-sky-200 ' +
                    'dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800'
                  }
                >
                  <AlertCircle className='h-3 w-3 mr-1' />
                  {jobCount} {jobCount === 1 ? 'job' : 'jobs'}
                </Badge>
                <Badge
                  variant='outline'
                  className={
                    'text-xs font-medium bg-sky-50 text-sky-700 border-sky-200 ' +
                    'dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800'
                  }
                >
                  <Car className='h-3 w-3 mr-1' />
                  {vehicleCount} {vehicleCount === 1 ? 'vehicle' : 'vehicles'}
                </Badge>
              </div>
            </div>
            {isOpen ? (
              <ChevronUp className='h-5 w-5 text-muted-foreground shrink-0' />
            ) : (
              <ChevronDown className='h-5 w-5 text-muted-foreground shrink-0' />
            )}
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className='px-4 pb-4 space-y-2'>
            {location.isCustomer && (
              <CustomerContactReveal contactId={location.contactId} context='stops_list' />
            )}
            {location.dispatcherNote && (
              <p className='text-xs text-muted-foreground whitespace-pre-line'>
                <span className='font-medium text-foreground'>Note for technician: </span>
                {location.dispatcherNote}
              </p>
            )}
            {vehicleEntries.map(([vinKey, vehicleStops], idx) => {
              const ids = vehicleStops.map(s => s.id);
              const isRemoving = ids.some(id => removingIds.has(id));
              // Guard behind the disabled button: never delete a row that is assigned.
              const removableIds = vehicleStops
                .filter(s => !assignedStops?.has(`${s.vin}|${s.campaign_number}`))
                .map(s => s.id);
              return (
                <VehicleCard
                  key={vinKey}
                  stops={vehicleStops}
                  index={idx}
                  onRemove={() => onRemoveVehicle(removableIds)}
                  isRemoving={isRemoving}
                  assignedStops={assignedStops}
                />
              );
            })}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ===========================================================================
// ManualStopsList — groups stops by location → by VIN
// ===========================================================================
// eslint-disable-next-line max-lines-per-function
export const ManualStopsList: React.FC<ManualStopsListProps> = ({
  stops,
  onRemove,
  assignedStops,
  scrollToLocation,
  onScrollComplete,
  kindFilter = 'all',
  onKindFilterChange,
  kindCounts
}) => {
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const locationRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // Incremented to force a LocationGroup to re-open even if the same name is clicked twice
  const [openTrigger, setOpenTrigger] = useState(0);
  const [triggeredLocation, setTriggeredLocation] = useState<string | null>(null);

  useEffect(() => {
    if (!scrollToLocation) return;
    setTriggeredLocation(scrollToLocation);
    setOpenTrigger(n => n + 1);
    // Defer scroll until after the group has expanded (next microtask)
    const id = setTimeout(() => {
      const el = locationRefs.current.get(scrollToLocation);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      onScrollComplete?.();
    }, 50);
    return () => clearTimeout(id);
  }, [scrollToLocation, onScrollComplete]);

  const groupedLocations = useMemo(() => {
    const locations = new Map<string, LocationGroupData>();
    stops.forEach(stop => {
      const locationKey = getStopLocationKey(stop);
      if (!locations.has(locationKey)) {
        locations.set(locationKey, {
          key: locationKey,
          name: getStopDisplayName(stop),
          isCustomer: isCustomerStop(stop),
          contactId: stop.contact_id ?? null,
          addressLine2: stop.address_line2 ?? null,
          dispatcherNote: stop.dispatcher_note ?? null,
          addressUnverified: UNVERIFIED_STATUSES.has(stop.address_validation?.status ?? ''),
          address: stop.dealer_address,
          ...(stop.dealer_city !== undefined && { city: stop.dealer_city }),
          ...(stop.dealer_region !== undefined && { region: stop.dealer_region }),
          ...(stop.dealer_postal_code !== undefined && { postcode: stop.dealer_postal_code }),
          ...(stop.dealer_phone && !isCustomerStop(stop) && { phone: stop.dealer_phone }),
          vehicles: new Map()
        });
      }
      const location = locations.get(locationKey);
      if (!location) return;
      const vinKey = stop.vin || stop.id;
      const existing = location.vehicles.get(vinKey);
      if (existing) {
        existing.push(stop);
      } else {
        location.vehicles.set(vinKey, [stop]);
      }
    });
    return Array.from(locations.values()).sort(
      (a, b) => a.name.localeCompare(b.name) || a.address.localeCompare(b.address)
    );
  }, [stops]);

  const handleRemoveVehicle = async (ids: string[]) => {
    if (ids.length === 0) return;
    setRemovingIds(new Set(ids));
    try {
      for (const id of ids) {
        await onRemove(id);
      }
    } finally {
      setRemovingIds(new Set());
    }
  };

  const showKindFilter =
    !!onKindFilterChange && !!kindCounts && kindCounts.business > 0 && kindCounts.customer > 0;

  if (stops.length === 0 && !showKindFilter) return null;

  return (
    <div className='space-y-4'>
      {showKindFilter && (
        <KindFilter value={kindFilter} counts={kindCounts} onChange={onKindFilterChange} />
      )}
      {groupedLocations.map(location => (
        <LocationGroup
          key={location.key}
          location={location}
          onRemoveVehicle={handleRemoveVehicle}
          removingIds={removingIds}
          assignedStops={assignedStops}
          openTrigger={triggeredLocation === location.key ? openTrigger : 0}
          outerRef={el => {
            if (el) locationRefs.current.set(location.key, el);
            else locationRefs.current.delete(location.key);
          }}
        />
      ))}
    </div>
  );
};

const KIND_OPTIONS: Array<{ value: ManualStopKindFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'business', label: 'Business' },
  { value: 'customer', label: 'Customer' }
];

/** Small segmented control; filters the list and the map's manual-stop markers. */
const KindFilter: React.FC<{
  value: ManualStopKindFilter;
  counts: { business: number; customer: number };
  onChange: (value: ManualStopKindFilter) => void;
}> = ({ value, counts, onChange }) => (
  <div role='radiogroup' aria-label='Manual stop type' className='flex gap-1 text-xs'>
    {KIND_OPTIONS.map(option => {
      const count =
        option.value === 'all'
          ? counts.business + counts.customer
          : counts[option.value as 'business' | 'customer'];
      const selected = value === option.value;
      return (
        <button
          key={option.value}
          type='button'
          role='radio'
          aria-checked={selected}
          onClick={() => onChange(option.value)}
          className={
            'px-2 py-1 rounded-md border transition-colors ' +
            (selected
              ? 'bg-[#0ea5e9]/10 border-[#0ea5e9]/40 text-foreground'
              : 'border-transparent text-muted-foreground hover:bg-muted')
          }
        >
          {option.label} <span className='tabular-nums'>{count}</span>
        </button>
      );
    })}
  </div>
);

export default ManualStopsList;
